import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiKey } from 'react-icons/fi';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  fetchPermissions
} from '../redux/slices/userSlice';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Pagination from '../components/common/Pagination';
import { toast } from 'react-toastify';
import './Users.css';

const initialFormState = {
  email: '',
  password: '',
  name: '',
  role: 'viewer',
  permissions: []
};

const allPermissions = [
  { name: 'view_transactions', label: 'View Transactions', description: 'View transaction list and details' },
  { name: 'approve_transactions', label: 'Approve Transactions', description: 'Approve pending transactions' },
  { name: 'reject_transactions', label: 'Reject Transactions', description: 'Reject pending transactions' },
  { name: 'manage_users', label: 'Manage Team', description: 'Create and manage team members' },
  { name: 'manage_webhooks', label: 'Manage Webhooks', description: 'Configure webhook endpoints' },
  { name: 'manage_ip_whitelist', label: 'Manage IP Whitelist', description: 'Manage allowed IP addresses' },
  { name: 'manage_projects', label: 'Manage Projects', description: 'Create and configure projects' },
  { name: 'view_logs', label: 'View Logs', description: 'View webhook and system logs' },
  { name: 'resend_webhooks', label: 'Resend Webhooks', description: 'Retry failed webhook deliveries' }
];

const Users = () => {
  const dispatch = useDispatch();
  const { users, pagination, loading } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState(initialFormState);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    dispatch(fetchUsers({ page: 1, limit: 20 }));
    dispatch(fetchPermissions());
  }, [dispatch]);

  const handlePermissionToggle = (permissionName) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionName)
        ? prev.permissions.filter(p => p !== permissionName)
        : [...prev.permissions, permissionName]
    }));
  };

  const handleOpenCreate = () => {
    setFormData(initialFormState);
    setModalMode('create');
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setFormData({
      name: user.name,
      role: user.role,
      is_active: user.is_active,
      permissions: user.permissions?.map(p => p.name) || []
    });
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'create') {
        await dispatch(createUser(formData)).unwrap();
        toast.success('User created successfully');
      } else {
        await dispatch(
          updateUser({ id: selectedUser.id, data: formData })
        ).unwrap();
        toast.success('User updated successfully');
      }
      setShowModal(false);
      setFormData(initialFormState);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteUser(selectedUser.id)).unwrap();
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await dispatch(
        resetUserPassword({ id: selectedUser.id, newPassword })
      ).unwrap();
      toast.success('Password reset successfully');
      setShowResetPassword(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error);
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'operator', label: 'Operator' },
    { value: 'viewer', label: 'Viewer' }
  ];

  const columns = [
    {
      key: 'name',
      title: 'Name',
      render: (value, row) => (
        <div className="user-cell">
          <div className="user-avatar">{value?.charAt(0)}</div>
          <div className="user-info">
            <span className="user-name">{value}</span>
            <span className="user-email">{row.email}</span>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      title: 'Role',
      render: (value) => <Badge variant={value}>{value}</Badge>
    },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <Badge variant={value ? 'success' : 'danger'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      key: 'last_login',
      title: 'Last Login',
      render: (value) =>
        value ? format(new Date(value), 'MMM dd, yyyy HH:mm') : 'Never'
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            onClick={() => handleOpenEdit(row)}
            title="Edit"
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn reset"
            onClick={() => {
              setSelectedUser(row);
              setShowResetPassword(true);
            }}
            title="Reset Password"
          >
            <FiKey />
          </button>
          {row.id !== currentUser?.id && (
            <button
              className="action-btn delete"
              onClick={() => {
                setSelectedUser(row);
                setShowDeleteConfirm(true);
              }}
              title="Delete"
            >
              <FiTrash2 />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <Layout title="Team Members">
      <div className="users-page">
        <Card
          title="Team Members"
          subtitle="Manage team members and their permissions"
          actions={
            <Button onClick={handleOpenCreate} size="small">
              <FiPlus /> Add Member
            </Button>
          }
        >
          <Table
            columns={columns}
            data={users}
            loading={loading}
            emptyMessage="No users found"
          />

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) => dispatch(fetchUsers({ page, limit: 20 }))}
          />
        </Card>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalMode === 'create' ? 'Add Team Member' : 'Edit Team Member'}
          size="large"
        >
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-row">
              <Input
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />

              <Select
                label="Role"
                name="role"
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                options={roleOptions}
                required
              />
            </div>

            {modalMode === 'create' && (
              <div className="form-row">
                <Input
                  type="email"
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                />

                <Input
                  type="password"
                  label="Password"
                  name="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </div>
            )}

            {modalMode === 'edit' && (
              <div className="status-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            )}

            <div className="permissions-section">
              <label className="section-label">Permissions</label>
              <p className="section-description">
                Select additional permissions for this team member. Role-based permissions are automatically assigned.
              </p>
              <div className="permissions-grid">
                {allPermissions.map(permission => (
                  <label key={permission.name} className="permission-item">
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(permission.name)}
                      onChange={() => handlePermissionToggle(permission.name)}
                    />
                    <div className="permission-info">
                      <span className="permission-label">{permission.label}</span>
                      <span className="permission-desc">{permission.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {modalMode === 'create' ? 'Add Member' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Reset Password Modal */}
        <Modal
          isOpen={showResetPassword}
          onClose={() => {
            setShowResetPassword(false);
            setNewPassword('');
          }}
          title="Reset Password"
          size="small"
        >
          <div className="reset-password-form">
            <p>Set a new password for {selectedUser?.name}</p>
            <Input
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters"
            />
            <div className="form-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowResetPassword(false);
                  setNewPassword('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleResetPassword} loading={loading}>
                Reset Password
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete User"
          message={`Are you sure you want to delete ${selectedUser?.name}? This action cannot be undone.`}
          confirmText="Delete"
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default Users;
